import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CheckCircle2, Crown, Mail, TrendingDown, TrendingUp, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, APIError } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { useSeo } from "@/lib/useSeo";
import { formatNumber, fromNowFR } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * /admin — signups dashboard. Answers one question at a glance : did we get
 * new users, and how many ? Same numbers as the Monday recap email
 * (POST /admin/recap/send fires it on demand). Access is enforced by the
 * gateway (ADMIN_EMAILS) ; this page only renders the 403 nicely.
 */
export default function AdminPage() {
  useSeo({ title: "Admin — inscriptions", canonicalPath: "/admin", noindex: true });
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const stats = useQuery({
    queryKey: ["admin", "signups"],
    queryFn: api.adminSignupStats,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });

  const [sending, setSending] = useState(false);
  const sendRecap = async () => {
    setSending(true);
    try {
      const r = await api.adminSendRecap();
      toast.success(`Récap envoyé à ${r.sent_to} (${r.last_7d} nouveaux sur 7 j).`);
    } catch (e) {
      toast.error(`Échec de l'envoi : ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const c = {
    primary: cssVar("--primary", "#3b82f6"),
    border: cssVar("--border", "#e5e7eb"),
    muted: cssVar("--foreground-muted", "#64748b"),
    surface: cssVar("--surface", "#ffffff"),
  };
  const tip = {
    contentStyle: {
      background: c.surface,
      border: `1px solid ${c.border}`,
      borderRadius: 8,
      color: c.muted,
      fontSize: 12,
    },
    itemStyle: { color: "var(--foreground)" },
    labelStyle: { color: "var(--foreground)", fontWeight: 600 },
    cursor: { fill: c.border, opacity: 0.4 },
  };

  if (!authLoading && !isAuthenticated) {
    return <Notice>Connectez-vous avec un compte administrateur pour voir cette page.</Notice>;
  }
  if (stats.isError) {
    const forbidden = stats.error instanceof APIError && stats.error.status === 403;
    return (
      <Notice>
        {forbidden
          ? "Accès réservé aux administrateurs."
          : `Impossible de charger les statistiques : ${(stats.error as Error).message}`}
      </Notice>
    );
  }

  const d = stats.data;
  const delta = d ? d.last_7d - d.prev_7d : 0;
  const kpis = d
    ? [
        {
          label: "Nouveaux (7 j)",
          value: formatNumber(d.last_7d),
          icon: UserPlus,
          tone: d.last_7d > 0 ? "text-success" : "text-foreground-muted",
          sub:
            d.prev_7d === 0 && d.last_7d === 0
              ? "aucun non plus la semaine passée"
              : `${delta > 0 ? "+" : ""}${delta} vs ${d.prev_7d} la semaine passée`,
          subIcon: delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : null,
        },
        { label: "Nouveaux (30 j)", value: formatNumber(d.last_30d), icon: UserPlus, tone: "text-info" },
        { label: "Total comptes", value: formatNumber(d.total), icon: Users, tone: "text-primary" },
        {
          label: "Onboarding terminé",
          value: formatNumber(d.onboarding_completed),
          icon: CheckCircle2,
          tone: "text-success",
          sub: d.total > 0 ? `${Math.round((d.onboarding_completed / d.total) * 100)} % des comptes` : undefined,
        },
        { label: "Comptes Pro", value: formatNumber(d.pro), icon: Crown, tone: "text-warning" },
      ]
    : [];

  return (
    <div className="p-4 md:p-6 flex flex-col gap-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Inscriptions</h1>
          <p className="text-sm text-foreground-muted">
            Comptes Boursicote créés. Un récap est envoyé par e-mail chaque lundi matin.
            {d && <> Mis à jour {fromNowFR(d.generated_at)}.</>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={sendRecap} disabled={sending || !d}>
          <Mail className="size-4" />
          {sending ? "Envoi…" : "Envoyer le récap maintenant"}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {stats.isLoading
          ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)
          : kpis.map((it) => {
              const Icon = it.icon;
              const SubIcon = it.subIcon ?? null;
              return (
                <Card key={it.label} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-xs text-foreground-muted">{it.label}</span>
                      <span className="text-2xl font-semibold tabular-nums text-foreground">{it.value}</span>
                      {it.sub && (
                        <span className="flex items-center gap-1 text-[11px] text-foreground-muted">
                          {SubIcon && <SubIcon className="size-3" />}
                          {it.sub}
                        </span>
                      )}
                    </div>
                    <Icon className={`size-5 shrink-0 ${it.tone}`} />
                  </div>
                </Card>
              );
            })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Inscriptions par jour</CardTitle>
            <CardDescription>30 derniers jours (heure de Paris)</CardDescription>
          </CardHeader>
          <CardContent className="h-56">
            {stats.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer>
                <BarChart data={d?.daily_30d ?? []}>
                  <CartesianGrid stroke={c.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="day"
                    stroke={c.muted}
                    tick={{ fontSize: 10, fill: c.muted }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
                    }
                    interval={4}
                  />
                  <YAxis allowDecimals={false} stroke={c.muted} tick={{ fontSize: 11, fill: c.muted }} width={28} />
                  <Tooltip
                    {...tip}
                    formatter={(v) => [String(v), "inscriptions"]}
                    labelFormatter={(v) =>
                      new Date(v).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })
                    }
                  />
                  <Bar dataKey="count" fill={c.primary} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inscriptions par semaine</CardTitle>
            <CardDescription>12 dernières semaines, semaines commençant le lundi</CardDescription>
          </CardHeader>
          <CardContent className="h-56">
            {stats.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer>
                <BarChart data={d?.weekly_12w ?? []}>
                  <CartesianGrid stroke={c.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="week_start"
                    stroke={c.muted}
                    tick={{ fontSize: 10, fill: c.muted }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
                    }
                  />
                  <YAxis allowDecimals={false} stroke={c.muted} tick={{ fontSize: 11, fill: c.muted }} width={28} />
                  <Tooltip
                    {...tip}
                    formatter={(v) => [String(v), "inscriptions"]}
                    labelFormatter={(v) =>
                      `Semaine du ${new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })}`
                    }
                  />
                  <Bar dataKey="count" fill={c.primary} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent signups */}
      <Card>
        <CardHeader>
          <CardTitle>Derniers inscrits</CardTitle>
          <CardDescription>20 comptes les plus récents</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {stats.isLoading ? (
            <div className="p-4 flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-foreground-muted">
                  <tr className="border-b border-border">
                    <th className="text-left font-medium px-4 py-2">E-mail</th>
                    <th className="text-left font-medium px-4 py-2">Inscrit</th>
                    <th className="text-left font-medium px-4 py-2">Onboarding</th>
                    <th className="text-left font-medium px-4 py-2">Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {(d?.recent ?? []).map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0 hover:bg-hover">
                      <td className="px-4 py-2 text-foreground">{r.email}</td>
                      <td className="px-4 py-2 text-foreground-muted whitespace-nowrap" title={r.created_at}>
                        {fromNowFR(r.created_at)}
                      </td>
                      <td className="px-4 py-2">
                        {r.onboarding_completed ? (
                          <span className="text-success">terminé</span>
                        ) : (
                          <span className="text-warning">incomplet</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-foreground-muted">{r.plan ?? "free"}</td>
                    </tr>
                  ))}
                  {d && d.recent.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-foreground-muted">
                        Aucun compte pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-6">
      <Card className="p-6 text-sm text-foreground-muted">{children}</Card>
    </div>
  );
}
